/**
 * 对象工作空间菜单配置
 * 进入具体客户、方案、项目后，左侧切换为工作空间菜单
 */
export type WorkspaceMenuItem = {
  key: string;
  /** 相对于工作空间根路径的子路径，例如 'profile' → /customer/:uuid/profile */
  subPath?: string;
  label: string;
  icon?: string;
  children?: WorkspaceMenuItem[];
};

export type WorkspaceMenuItemWithPath = Omit<WorkspaceMenuItem, 'children'> & {
  path?: string;
  children?: WorkspaceMenuItemWithPath[];
};

/** 客户工作空间菜单（/customer/:customerUuid/*） */
export const CUSTOMER_WORKSPACE_MENU: WorkspaceMenuItem[] = [
  { key: 'overview', subPath: '', label: '概览', icon: 'home' },
  {
    key: 'judgment',
    label: '客户判断',
    icon: 'audit',
    children: [
      { key: 'admission', subPath: 'admission', label: '准入评估' },
      {
        key: 'stage',
        subPath: 'stage',
        label: '转型阶段判断',
      },
      {
        key: 'decision-budget',
        subPath: 'decision-budget',
        label: '决策链与预算',
      },
      {
        key: 'leader-focus',
        subPath: 'leader-focus',
        label: '领导层关注点',
      },
      { key: 'risk', subPath: 'risk', label: '风险判断' },
    ],
  },
  {
    key: 'intelligence',
    label: '客户情报',
    icon: 'team',
    children: [
      { key: 'profile', subPath: 'profile', label: '基础信息' },
      { key: 'organization', subPath: 'organization', label: '业务板块图谱' },
      { key: 'key-people', subPath: 'key-people', label: '关键人员' },
      { key: 'tags', subPath: 'tags', label: '行业属性标签' },
      { key: 'system', subPath: 'system', label: '已有系统清单' },
      { key: 'material', subPath: 'material', label: '资料归集与摘要' },
    ],
  },
  {
    key: 'research-diagnosis',
    label: '调研诊断',
    icon: 'fileSearch',
    children: [
      { key: 'research', subPath: 'research', label: '历史接触记录' },
      { key: 'diagnosis', subPath: 'diagnosis', label: '诊断会话' },
      { key: 'problem-map', subPath: 'problem-map', label: '已知问题' },
      { key: 'evidence', subPath: 'evidence', label: '证据材料' },
    ],
  },
  {
    key: 'solution-business',
    label: '方案商务',
    icon: 'rise',
    children: [
      {
        key: 'solution-roadmap',
        subPath: 'solution-roadmap',
        label: '方案路线',
      },
      { key: 'application-map', subPath: 'application-map', label: '应用地图' },
      {
        key: 'methodology',
        subPath: 'methodology',
        label: '方法论推荐',
      },
      { key: 'quote-basis', subPath: 'quote-basis', label: '报价依据' },
      {
        key: 'business-material',
        subPath: 'business-material',
        label: '汇报材料',
      },
    ],
  },
  {
    key: 'evolution',
    label: '建设演进',
    icon: 'folderOpen',
    children: [
      { key: 'opportunity-map', subPath: 'opportunity-map', label: '机会地图' },
      { key: 'project', subPath: 'project', label: '关联项目' },
      { key: 'stage-review', subPath: 'stage-review', label: '阶段复盘' },
      {
        key: 'phase-two-opportunity',
        subPath: 'phase-two-opportunity',
        label: '二期机会',
      },
      { key: 'asset', subPath: 'asset', label: '沉淀资产' },
    ],
  },
];

/** 方案工作空间菜单（/solution/:solutionUuid/*） */
export const SOLUTION_WORKSPACE_MENU: WorkspaceMenuItem[] = [
  { key: 'overview', subPath: '', label: '方案概览', icon: 'home' },
  {
    key: 'research',
    subPath: 'research',
    label: '调研诊断',
    icon: 'fileSearch',
  },
  {
    key: 'problem-map',
    subPath: 'problem-map',
    label: '问题地图',
    icon: 'cluster',
  },
  {
    key: 'blueprint',
    subPath: 'blueprint',
    label: '业务蓝图',
    icon: 'branches',
  },
  {
    key: 'application-planning',
    subPath: 'application-planning',
    label: '应用规划',
    icon: 'appstore',
  },
  { key: 'roadmap', subPath: 'roadmap', label: '实施路线', icon: 'schedule' },
  {
    key: 'estimation',
    subPath: 'estimation',
    label: '工作量估算',
    icon: 'calculator',
  },
  { key: 'quote', subPath: 'quote', label: '报价管理', icon: 'fileDone' },
  { key: 'review', subPath: 'review', label: '评审决策', icon: 'audit' },
  { key: 'material', subPath: 'material', label: '商务材料', icon: 'folder' },
  { key: 'convert', subPath: 'convert', label: '转项目', icon: 'arrowRight' },
];

/** 项目工作空间菜单（/delivery/project/:projectUuid/*） */
export const PROJECT_WORKSPACE_MENU: WorkspaceMenuItem[] = [
  { key: 'overview', subPath: '', label: '项目概览', icon: 'home' },
  {
    key: 'governance',
    subPath: 'governance',
    label: '项目治理',
    icon: 'schedule',
  },
  { key: 'design', subPath: 'design', label: '需求设计', icon: 'fileSearch' },
  {
    key: 'development',
    subPath: 'development',
    label: '开发构建',
    icon: 'code',
  },
  {
    key: 'contribution',
    subPath: 'contribution',
    label: '代码活动',
    icon: 'branches',
  },
  { key: 'quality', subPath: 'quality', label: '测试验收', icon: 'bug' },
  { key: 'release', subPath: 'release', label: '上线移交', icon: 'cloud' },
  {
    key: 'retrospective',
    subPath: 'retrospective',
    label: '复盘沉淀',
    icon: 'audit',
  },
];

/** 根据工作空间类型和 uuid，生成完整的菜单路径列表 */
const buildWorkspaceMenuPath = (
  prefix: string,
  uuid: string,
  item: WorkspaceMenuItem,
) => {
  if (item.children?.length && item.subPath === undefined) {
    return undefined;
  }
  return item.subPath
    ? `${prefix}/${uuid}/${item.subPath}`
    : `${prefix}/${uuid}`;
};

export const buildCustomerWorkspaceMenu = (
  customerUuid: string,
): WorkspaceMenuItemWithPath[] => {
  const mapItem = (item: WorkspaceMenuItem): WorkspaceMenuItemWithPath => ({
    ...item,
    path: buildWorkspaceMenuPath('/customer', customerUuid, item),
    children: item.children?.map(mapItem),
  });

  return CUSTOMER_WORKSPACE_MENU.map(mapItem);
};

export const buildSolutionWorkspaceMenu = (solutionUuid: string) =>
  SOLUTION_WORKSPACE_MENU.map((item) => ({
    ...item,
    path: item.subPath
      ? `/solution/${solutionUuid}/${item.subPath}`
      : `/solution/${solutionUuid}`,
  }));

export const buildProjectWorkspaceMenu = (projectUuid: string) =>
  PROJECT_WORKSPACE_MENU.map((item) => ({
    ...item,
    path: item.subPath
      ? `/delivery/project/${projectUuid}/${item.subPath}`
      : `/delivery/project/${projectUuid}`,
  }));
